import { BudgetLineObject } from "@/objects/BudgetLineObject";
import { ExecutionObject } from "@/objects/ExecutionObject";
import { FinancialAccountObject } from "@/objects/FinancialAccountObject";
import { FinancialInstitutionObject } from "@/objects/FinancialInstitutionObject";
import { ChartedAccountObject } from "@/objects/ChartedAccountObject";
import { ThreadObject } from "@/objects/ThreadObject";
import { ContactObject } from "@/objects/ContactObject";
import { OrganizationObject } from "@/objects/OrganizationObject";
import { DocumentTemplateObject } from "@/objects/DocumentTemplateObject";
import { DocumentObject } from "@/objects/DocumentObject";
import { EntityObject } from "@/objects/EntityObject";
import { EntitySettingsObject } from "@/objects/EntitySettingsObject";
import { JournalEntryObject } from "@/objects/JournalEntryObject";
import { NotificationObject } from "@/objects/NotificationObject";
import { PublicDocumentObject } from "@/objects/PublicDocumentObject";
import { CategoryObject } from "@/objects/CategoryObject";
import { TagGroupObject } from "@/objects/TagGroupObject";
import { TagObject } from "@/objects/TagObject";
import { TaxObject } from "@/objects/TaxObject";
import { TransactionObject } from "@/objects/TransactionObject";
import { UserProfileObject } from "@/objects/UserProfileObject";
import { UserSettingsObject } from "@/objects/UserSettingsObject";
import { RuleDelegationObject } from "@/objects/RuleDelegationObject";
import { RuleDefinitionObject } from "@/objects/RuleDefinitionObject";
import { ReportObject } from "@/objects/ReportObject";
import { ReportObjects } from "@/objects/ReportSourceObjects";

import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import type { ObjectType, ReportSourceObjectType } from "@/objects/@types";
import { idToIdentifier } from "@/objects/@utils";
import { State } from "@/state";

const Objects = {
  BudgetLineObject: BudgetLineObject,
  ExecutionObject: ExecutionObject,
  FinancialAccountObject: FinancialAccountObject,
  FinancialInstitutionObject: FinancialInstitutionObject,
  ThreadObject: ThreadObject,
  ChartedAccountObject: ChartedAccountObject,
  ContactObject: ContactObject,
  OrganizationObject: OrganizationObject,
  DocumentTemplateObject: DocumentTemplateObject,
  DocumentObject: DocumentObject,
  EntityObject: EntityObject,
  EntitySettingsObject: EntitySettingsObject,
  JournalEntryObject: JournalEntryObject,
  NotificationObject: NotificationObject,
  CategoryObject: CategoryObject,
  TagObject: TagObject,
  TagGroupObject: TagGroupObject,
  TaxObject: TaxObject,
  TransactionObject: TransactionObject,
  UserProfileObject: UserProfileObject,
  UserSettingsObject: UserSettingsObject,
  RuleDelegationObject: RuleDelegationObject,
  RuleDefinitionObject: RuleDefinitionObject,
  ReportObject: ReportObject,
  PublicDocumentObject: PublicDocumentObject,
};

export { Objects, ReportObjects };

/** What a report source's rows are: a record type, or something worked out. */
export function objectForReportSource(
  source: string | null | undefined,
): ObjectType<any> | ReportSourceObjectType | undefined {
  if (!source) return undefined;
  const everything: Array<ObjectType<any> | ReportSourceObjectType> = [
    ...Object.values(Objects),
    ...Object.values(ReportObjects),
  ];
  return everything.find((one) => one.report?.source === source);
}

/**
 * Returns the reference value options for a given typename.
 * @param typename - The typename of the object.
 * @returns The reference value options.
 */
export const getReferenceValueOptionsByTypename = (typename: string) => {
  return (
    entityIdentifiers?: string[],
    purpose: "value" | "filter" = "value",
  ) => {
    // filter out undefined or null entity identifiers
    let filteredEntityIdentifiers = (entityIdentifiers || [])?.filter(
      (identifier) => identifier !== undefined && identifier !== null,
    );

    const { scopedEntityIdentifier, activeEntityIdentifiers } =
      State.getState();

    if (filteredEntityIdentifiers?.length === 0) {
      if (scopedEntityIdentifier) {
        filteredEntityIdentifiers = [scopedEntityIdentifier];
      } else {
        filteredEntityIdentifiers = activeEntityIdentifiers?.slice(0, 1);
      }
    }

    const object = Objects[typename];
    const options = object?.getReferenceValueOptions(
      filteredEntityIdentifiers,
      purpose,
    );
    // A filter looks across every active business, so each record it offers
    // starts with the box of the business it is in, drawn as the list's own
    // business column draws it. An entity's own rows are that box already.
    if (purpose !== "filter" || !options?.onRenderOption || object.isEntity) {
      return options;
    }
    const { onRenderOption } = options;
    return {
      ...options,
      onRenderOption: (...args: Parameters<typeof onRenderOption>) => {
        const entityIdentifier = idToIdentifier(object.getEntityId(args[0]));
        return (
          <div className="flex items-center gap-2 min-w-0">
            {entityIdentifier && (
              <BoxForEntity
                identifier={entityIdentifier}
                representation="character"
              />
            )}
            <div className="min-w-0 flex-1">{onRenderOption(...args)}</div>
          </div>
        );
      },
    };
  };
};

/**
 * Returns an object containing all creatable objects from the Objects collection.
 * An object is considered creatable if it has at least one field with createProps defined.
 *
 * @returns {Record<string, ObjectType<any>>} An object mapping object keys to their corresponding ObjectType definitions
 * @example
 * const creatableObjects = getCreateableObjects();
 * // Returns { JournalEntryObject: {...}, TransactionObject: {...} }
 */
export const getCreatableObjects = () => {
  return Object.keys(Objects).reduce((acc, objectKey) => {
    const objectDefinition = Objects[objectKey as keyof typeof Objects];
    if (objectDefinition.fieldDefinitions.some((field) => field.createProps)) {
      acc[objectKey] = objectDefinition;
    }
    return acc;
  }, {});
};

/**
 * Returns an object containing all updatable objects from the Objects collection.
 * An object is considered updatable if it has at least one field with updateProps defined.
 *
 * @returns {Record<string, ObjectType<any>>} An object mapping object keys to their corresponding ObjectType definitions
 * @example
 * const updatableObjects = getUpdatableObjects();
 * // Returns { JournalEntryObject: {...}, TransactionObject: {...} }
 */
export const getUpdatableObjects = () => {
  return Object.keys(Objects).reduce((acc, objectKey) => {
    const objectDefinition = Objects[objectKey as keyof typeof Objects];
    if (objectDefinition.fieldDefinitions.some((field) => field.updateProps)) {
      acc[objectKey] = objectDefinition;
    }
    return acc;
  }, {});
};
